/**
 * axios HTTP 클라이언트 (Phase F.1 갱신).
 *
 * 인증:
 *   - access token (15분 만료) → 모든 요청에 Authorization 헤더로
 *   - refresh token (14일) → 401 응답 시 자동 갱신 (rotation)
 *   - 갱신 중 동시 요청은 단일 promise 공유로 한 번만 갱신
 *
 * 에러:
 *   - 백엔드 ProblemDetails(`{code, message, details, ...}`) 응답을 그대로 throw
 *   - 호출측은 `extractApiError(err)`로 한국어 메시지 추출
 */
import axios, { AxiosError, type AxiosRequestConfig } from 'axios'

const baseURL =
  (typeof window !== 'undefined' && (window as any).__BACKEND_URL__) ||
  (import.meta.env.VITE_BACKEND_URL as string | undefined) ||
  ''

export const api = axios.create({
  baseURL,
  timeout: 60_000,
})

// ─────────────────────────────────────────────────────────────────
// Token storage
// ─────────────────────────────────────────────────────────────────

const ACCESS_KEY = 'seolhwa.access_token'
const REFRESH_KEY = 'seolhwa.refresh_token'

export function getAccessToken(): string | null {
  try {
    return localStorage.getItem(ACCESS_KEY)
  } catch {
    return null
  }
}
export function getRefreshToken(): string | null {
  try {
    return localStorage.getItem(REFRESH_KEY)
  } catch {
    return null
  }
}
export function setTokens(access: string | null, refresh?: string | null) {
  try {
    if (access) localStorage.setItem(ACCESS_KEY, access)
    else localStorage.removeItem(ACCESS_KEY)
    if (refresh !== undefined) {
      if (refresh) localStorage.setItem(REFRESH_KEY, refresh)
      else localStorage.removeItem(REFRESH_KEY)
    }
  } catch {
    /* noop */
  }
}

// 호환: 옛 함수 시그니처 (점진 마이그레이션용)
export const getStoredToken = getAccessToken
export const setStoredToken = (t: string | null) => setTokens(t)

// ─────────────────────────────────────────────────────────────────
// Request — Authorization 헤더
// ─────────────────────────────────────────────────────────────────

api.interceptors.request.use((config) => {
  const token = getAccessToken()
  if (token) {
    config.headers = config.headers ?? {}
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// ─────────────────────────────────────────────────────────────────
// Response — 401 → refresh 자동 갱신 (rotation)
// ─────────────────────────────────────────────────────────────────

interface RefreshResponse {
  access_token: string
  refresh_token: string
  token_type: string
  expires_in: number
  refresh_expires_in: number
}

let refreshPromise: Promise<string | null> | null = null

async function attemptRefresh(): Promise<string | null> {
  const refresh = getRefreshToken()
  if (!refresh) return null
  // 단일 in-flight refresh 공유
  if (refreshPromise) return refreshPromise
  refreshPromise = axios
    .create({ baseURL })
    .post<RefreshResponse>('/api/v1/auth/refresh', { refresh_token: refresh })
    .then((res) => {
      const { access_token, refresh_token } = res.data
      setTokens(access_token, refresh_token)
      return access_token
    })
    .catch(() => {
      // refresh 실패 → 모든 토큰 폐기
      setTokens(null, null)
      return null
    })
    .finally(() => {
      refreshPromise = null
    })
  return refreshPromise
}

api.interceptors.response.use(
  (r) => r,
  async (err: AxiosError) => {
    const status = err?.response?.status
    const original = err.config as AxiosRequestConfig & { _retried?: boolean }

    // 401 + refresh가 가능하고 + 한 번도 재시도 안 한 요청만
    if (
      status === 401 &&
      original &&
      !original._retried &&
      original.url !== '/api/v1/auth/refresh' &&
      original.url !== '/api/v1/auth/login'
    ) {
      original._retried = true
      const newAccess = await attemptRefresh()
      if (newAccess) {
        original.headers = {
          ...(original.headers ?? {}),
          Authorization: `Bearer ${newAccess}`,
        } as any
        return api.request(original)
      }
      // refresh 실패 — 호출측이 401 처리 (로그인 화면으로)
    }

    // 다른 401 (refresh 자체 실패 등) — 토큰 정리
    if (status === 401) {
      setTokens(null, null)
    }
    return Promise.reject(err)
  },
)

// ─────────────────────────────────────────────────────────────────
// 에러 추출 — Phase 1.6 ProblemDetails 응답 → 한국어 메시지
// ─────────────────────────────────────────────────────────────────

export interface ApiErrorDetails {
  code: string
  message: string
  status: number
  type?: string
  request_id?: string
  details?: unknown
}

/** axios 에러에서 한국어 ProblemDetails 추출. fallback 메시지 포함. */
export function extractApiError(err: unknown): ApiErrorDetails {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data
    if (data && typeof data === 'object' && 'code' in data && 'message' in data) {
      return {
        code: String((data as any).code),
        message: String((data as any).message),
        status: err.response?.status ?? 0,
        type: (data as any).type,
        request_id: (data as any).request_id,
        details: (data as any).details,
      }
    }
    // 백엔드 다운 / CORS / 네트워크
    if (!err.response) {
      return {
        code: 'NETWORK_ERROR',
        message: '서버에 연결할 수 없습니다. 네트워크 상태를 확인해 주세요.',
        status: 0,
      }
    }
    return {
      code: 'HTTP_ERROR',
      message: `요청 실패 (HTTP ${err.response.status})`,
      status: err.response.status,
    }
  }
  return {
    code: 'UNKNOWN_ERROR',
    message: err instanceof Error ? err.message : String(err),
    status: 0,
  }
}
